import { useLocale, useTranslations } from "use-intl";

import { Link } from "@/components/link";
import type { CuratedStyleIndexEntry } from "@/features/garimpos/garimpos-query";
import { groupStyleIndex } from "@/features/garimpos/style-index-groups";
import { useFormatNumber } from "@/hooks/use-format-number";
import { formatTranslatedCatalogTaxonomyLabel } from "@/lib/music-taxonomy";

const SECTION_ID = "garimpos-styles";

function letterId(letter: string): string {
  return `${SECTION_ID}-${letter === "#" ? "other" : letter.toLowerCase()}`;
}

/** Compact A–Z list of every style with live offers, each a followed link to its offers page. */
export function StyleIndex({ entries }: { entries: readonly CuratedStyleIndexEntry[] }) {
  const t = useTranslations("garimpos");
  const tStyles = useTranslations("shared.styles");
  const locale = useLocale();
  const formatNumber = useFormatNumber();
  const groups = groupStyleIndex(
    entries,
    (key) => formatTranslatedCatalogTaxonomyLabel(key, tStyles),
    locale,
  );

  if (!groups.length) return null;

  return (
    <section
      id={SECTION_ID}
      aria-labelledby={`${SECTION_ID}-title`}
      className="scroll-mt-24 border-t border-border pt-10 sm:pt-12"
    >
      <h2 id={`${SECTION_ID}-title`} className="text-3xl font-bold tracking-[-0.03em] sm:text-4xl">
        {t("styleIndex.title")}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("styleIndex.description")}</p>

      <nav aria-label={t("styleIndex.jumpLabel")} className="mt-5">
        <ul className="flex flex-wrap gap-1">
          {groups.map((group) => (
            <li key={group.letter}>
              <a
                href={`#${letterId(group.letter)}`}
                className="relative after:absolute after:-inset-0.5 after:content-[''] inline-flex size-10 items-center justify-center rounded-lg text-sm font-bold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {group.letter}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-6 columns-2 gap-x-8 sm:columns-3 lg:columns-4 xl:columns-5">
        {groups.map((group) => (
          <div
            key={group.letter}
            id={letterId(group.letter)}
            className="mb-6 scroll-mt-24 break-inside-avoid"
          >
            <h3 className="border-b border-border pb-1 text-sm font-bold">{group.letter}</h3>
            <ul className="mt-1.5">
              {group.items.map((item) => (
                <li key={item.key}>
                  <Link
                    to="/offers"
                    search={{ style: [item.key] }}
                    className="flex min-h-11 items-center justify-between gap-3 rounded-md py-1.5 text-sm md:min-h-0 md:items-baseline underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {item.label}
                    <span
                      aria-hidden="true"
                      className="shrink-0 text-xs text-muted-foreground tabular-nums"
                    >
                      {formatNumber(item.count)}
                    </span>
                    <span className="sr-only">{t("offerCount", { count: item.count })}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
